import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Download, Loader2, RotateCcw, Gauge } from 'lucide-react';
import apiClient from '../../../lib/apiClient';
import { cn } from '../../../utils/tailwindUtils';


const SPEEDS = [1, 1.25, 1.5, 2];


function formatTime(secs) {
  if (!secs || !isFinite(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}


/**
 * Custom audio player for an AI-Caller recording.
 *
 * Recordings are hosted by Twilio and proxied through our backend, so the
 * browser never talks to the telephony provider directly. We fetch the audio
 * via our authenticated apiClient, wrap it in a Blob → object-URL, and drive a
 * custom <audio> element with a modern Tailwind UI (play/pause, scrub, speed,
 * volume, download).
 *
 * Props:
 *   callId      — AICallLog.id
 *   endpoint    — API path template; `{id}` is replaced by callId.
 *                 Defaults to the Engage proxy; pass 'ai-caller/recording/{id}/'
 *                 for the TrustEdge side.
 *   variant     — 'compact' (default, timeline inline) | 'expanded' (call-detail page)
 *   className   — extra classes on the wrapper
 */
export default function RecordingPlayer({
  callId,
  endpoint = 'engage/ai-caller/calls/{id}/recording/',
  variant = 'compact',
  className,
}) {
  const audioRef = useRef(null);
  const [blobUrl, setBlobUrl]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);

  const [playing, setPlaying]         = useState(false);
  const [current, setCurrent]         = useState(0);
  const [duration, setDuration]       = useState(0);
  const [volume, setVolume]           = useState(1);
  const [muted, setMuted]             = useState(false);
  const [speed, setSpeed]             = useState(1);

  // ── Fetch the audio blob through the authenticated proxy ─────────────
  useEffect(() => {
    if (!callId) return undefined;
    let objectUrl = null;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const url = endpoint.replace('{id}', String(callId));
        const blob = await apiClient.get(url, { responseType: 'blob' });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.status === 404
          ? 'Recording is still being processed — check back in ~30s.'
          : 'Could not load recording.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [callId, endpoint]);

  // ── Audio event wiring ────────────────────────────────────────────────
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const onTime  = () => setCurrent(a.currentTime);
    const onMeta  = () => setDuration(a.duration || 0);
    const onEnded = () => setPlaying(false);
    const onPlay  = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('durationchange', onMeta);
    a.addEventListener('ended', onEnded);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('durationchange', onMeta);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
    };
  }, [blobUrl]);

  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = speed; }, [speed]);
  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
    audioRef.current.muted = muted;
  }, [volume, muted]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  }, []);

  const restart = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.play().catch(() => {});
  }, []);

  const seekPct = useCallback((pct) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    a.currentTime = duration * pct;
    setCurrent(a.currentTime);
  }, [duration]);

  const cycleSpeed = useCallback(() => {
    const i = SPEEDS.indexOf(speed);
    setSpeed(SPEEDS[(i + 1) % SPEEDS.length]);
  }, [speed]);

  const download = useCallback(() => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `ai-call-${callId}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [blobUrl, callId]);

  // ── Error state ───────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={cn(
        'mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/60',
        className,
      )}>
        <span>{error}</span>
      </div>
    );
  }

  // ── Loading state ─────────────────────────────────────────────────────
  if (loading || !blobUrl) {
    return (
      <div className={cn(
        'mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] text-gray-500 bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700',
        className,
      )}>
        <Loader2 className="w-3.5 h-3.5 text-teal-500 animate-spin flex-shrink-0" />
        <span>Loading recording…</span>
      </div>
    );
  }

  const pct = duration ? (current / duration) * 100 : 0;
  const compact = variant === 'compact';

  return (
    <div className={cn(
      'mt-2 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border border-teal-100 dark:border-teal-800/60 shadow-sm overflow-hidden',
      className,
    )}>
      <audio ref={audioRef} src={blobUrl} preload="metadata" className="hidden" />

      <div className={cn('flex items-center gap-2.5', compact ? 'px-2.5 py-2' : 'px-3 py-2.5')}>
        {/* Play / Pause */}
        <button
          type="button"
          onClick={togglePlay}
          className={cn(
            'flex-shrink-0 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-sm hover:from-teal-600 hover:to-cyan-700 flex items-center justify-center transition-transform active:scale-95',
            compact ? 'w-8 h-8' : 'w-10 h-10',
          )}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {playing ? <Pause className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} /> : <Play className={cn(compact ? 'w-3.5 h-3.5' : 'w-4 h-4', 'ml-0.5')} />}
        </button>

        {/* Progress bar */}
        <div
          className="flex-1 min-w-0 group"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            seekPct((e.clientX - rect.left) / rect.width);
          }}
        >
          <div className={cn(
            'relative w-full rounded-full bg-gray-200 dark:bg-gray-700 cursor-pointer overflow-hidden',
            compact ? 'h-1.5' : 'h-2',
          )}>
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 transition-[width] duration-100"
              style={{ width: `${pct}%` }}
            />
            <div
              className={cn(
                'absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full bg-white border-2 border-teal-500 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity',
                compact ? 'w-3 h-3' : 'w-3.5 h-3.5',
              )}
              style={{ left: `${pct}%` }}
            />
          </div>
        </div>

        {/* Time */}
        <span className={cn(
          'flex-shrink-0 font-mono tabular-nums text-gray-600 dark:text-gray-300',
          compact ? 'text-[10px]' : 'text-[11px]',
        )}>
          {formatTime(current)} / {formatTime(duration)}
        </span>

        {/* Speed */}
        <button
          type="button"
          onClick={cycleSpeed}
          title="Playback speed"
          className={cn(
            'flex-shrink-0 inline-flex items-center gap-0.5 rounded-full font-medium text-teal-700 dark:text-teal-300 bg-white/70 dark:bg-gray-800/60 hover:bg-white hover:shadow-sm border border-teal-200 dark:border-teal-800/60 transition-colors',
            compact ? 'text-[10px] px-1.5 py-0.5' : 'text-[11px] px-2 py-1',
          )}
        >
          <Gauge className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} />
          {speed}×
        </button>

        {/* Restart */}
        <button
          type="button"
          onClick={restart}
          title="Restart from beginning"
          className={cn(
            'flex-shrink-0 rounded-full text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-white dark:hover:bg-gray-800/60 transition-colors',
            compact ? 'p-1' : 'p-1.5',
          )}
        >
          <RotateCcw className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        </button>

        {/* Volume (expanded only) */}
        {!compact && (
          <div className="flex-shrink-0 flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setMuted(m => !m)}
              title={muted ? 'Unmute' : 'Mute'}
              className="p-1.5 rounded-full text-gray-500 hover:text-teal-600 hover:bg-white dark:hover:bg-gray-800/60 transition-colors"
            >
              {muted || volume === 0 ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
            </button>
            <input
              type="range" min="0" max="1" step="0.05"
              value={muted ? 0 : volume}
              onChange={(e) => { setVolume(Number(e.target.value)); setMuted(false); }}
              className="w-16 accent-teal-500"
            />
          </div>
        )}

        {/* Download */}
        <button
          type="button"
          onClick={download}
          title="Download recording"
          className={cn(
            'flex-shrink-0 rounded-full text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-white dark:hover:bg-gray-800/60 transition-colors',
            compact ? 'p-1' : 'p-1.5',
          )}
        >
          <Download className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        </button>
      </div>
    </div>
  );
}
